import $ from 'jquery';
import DEFAULTS from './defaults';

const NAMESPACE = 'CFDynamicMenu';

class CFDynamicMenu {
  constructor(element, options) {
    this.element = element;
    this.$element = $(element);

    this.options = $.extend(true, {}, DEFAULTS, options, this.$element.data());

    this.namespace = this.options.namespace;

    this.$element.addClass(this.namespace);

    this.trigger('beforeInit');

    this.initMenuItemsOnClickEvent();
    this.refreshProgress();

    this.trigger('afterInit');
  }

  initMenuItemsOnClickEvent() {
    this.$element.find('a').on('click', (events) => {
      const $a = $(events.currentTarget);

      // Set anchor parent li as active.
      this.activateMenuItem($a.parents(`${this.options.menuItemElement}:first`));
    });
  }

  activateMenuItem(menuItem) {
    const $menuItem = this.resolveMenuElement(menuItem);
    const $menuItemParentsLi = $menuItem.parents(this.options.menuItemElement);

    // Set all other li as inactive.
    this.$element.find(this.options.menuItemElement)
      .removeClass(this.options.currentMenuItemClass)
      .removeClass(this.options.currentMenuAncestorClass)
      .removeClass(this.options.currentMenuParentClass);

    // Set menu item as active.
    $menuItem.addClass(this.options.currentMenuItemClass);

    $menuItemParentsLi.addClass(this.options.currentMenuAncestorClass);

    $menuItemParentsLi.filter(':first').addClass(this.options.currentMenuParentClass);

    this.refreshProgress();
  }

  refreshProgress() {
    const $currentMenuItem = this.$element.find(`.${this.options.currentMenuItemClass}`);

    // Deactivate all menu items.
    this.$element.find(`.${this.options.activeMenuItemClass}`)
      .removeClass(this.options.activeMenuItemClass);

    // Activate current menu item.
    $currentMenuItem.addClass(this.options.activeMenuItemClass);

    // Activate all previous menu items on the same level and their children.
    $currentMenuItem.prevAll(this.options.menuItemElement)
      .addClass(this.options.activeMenuItemClass)
      .find(this.options.menuItemElement)
      .addClass(this.options.activeMenuItemClass);

    $currentMenuItem.parentsUntil('.navbar-nav', this.options.menuItemElement)
      .addClass(this.options.activeMenuItemClass)
      .prevAll()
      .addClass(this.options.activeMenuItemClass)
      .find(this.options.menuItemElement)
      .addClass(this.options.activeMenuItemClass);
  }

  /**
   * Add submenu items after a submenu item.
   *
   * @param {string|object} submenuItem  A selector string or the object of an element
   *                                     from within the menu element.
   * @param {string}        submenuItems Submenu items HTML string.
   */
  addSubmenuItemsAfterSubmenuItem(submenuItem, submenuItems) {
    const $submenuItem = this.resolveMenuElement(submenuItem);

    // No submenu item has been found.
    if (!$submenuItem.length) {
      return;
    }

    this.trigger('beforeAddSubmenuItemsAfterSubmenuItem');

    $submenuItem.after(submenuItems);

    this.trigger('afterAddSubmenuItemsAfterSubmenuItem');
  }

  /**
   * Add submenu items to a provided menu item.
   *
   * @param {string|object} menuItem     A selector string or the object of an element
   *                                     from within the menu element.
   * @param {string}        submenuItems Submenu items HTML string.
   */
  addSubmenuItems(menuItem, submenuItems) {
    const $submenu = this.resolveMenuElementSubmenu(menuItem);

    // No submenu has been found or created.
    if (!$submenu.length) {
      return;
    }

    this.trigger('beforeAddSubmenuItems');

    $submenu.append(submenuItems);

    this.trigger('afterAddSubmenuItems');
  }

  /**
   * Update the submenu items of a provided menu item.
   *
   * @param {string|object} menuItem     A selector string or the object of an element
   *                                     from within the menu element.
   * @param {string}        submenuItems Submenu items HTML string.
   */
  updateSubmenuItems(menuItem, submenuItems) {
    const $submenu = this.resolveMenuElementSubmenu(menuItem);

    // No submenu has been found or created.
    if (!$submenu.length) {
      return;
    }

    this.trigger('beforeUpdateSubmenuItems');

    $submenu.html(submenuItems);

    this.trigger('afterUpdateSubmenuItems');
  }

  /**
   * Resolve a selector string or the object of an element to a jQuery object.
   *
   * @param {string|object} menuElement A selector string or the object of an element
   *                                    from within the menu element.
   *
   * @returns {jQuery}
   */
  resolveMenuElement(menuElement) {
    let $menuElement;
    if (typeof menuElement === 'string') {
      // Look for the selector inside the menu.
      $menuElement = this.$element.find(menuElement);
    } else {
      $menuElement = $(menuElement);
    }

    return $menuElement;
  }

  /**
   * Get the submenu for the provided menu item or create a new one and attach it to the menu item
   * if it doesn't already exist.
   *
   * @param {string|object} menuItem A selector string or the object of an element
   *                                 from within the menu element.
   *
   * @returns {boolean|jQuery} Returns false if the menu item could not be found or
   *                           the jQuery object of the existing/newly created submenu.
   */
  resolveMenuElementSubmenu(menuItem) {
    const $menuItem = this.resolveMenuElement(menuItem);

    // No menu item found.
    if (!$menuItem.length) {
      return false;
    }

    $menuItem.addClass(this.options.menuItemHasChildrenClass);

    let $submenu = $menuItem.find(`> ${this.options.submenuElement}${this.options.submenuClass ? `.${this.options.submenuClass}` : ''}`);

    if (!$submenu.length) {
      // Create the submenu if it doesn't already exist.
      $submenu = $(`<${this.options.submenuElement}>`).addClass(this.options.submenuClass);

      // Add the submenu to the menu item.
      $menuItem.append($submenu);
    }

    return $submenu;
  }

  trigger(eventType, ...params) {
    const data = [this].concat(params);

    // Event.
    this.$element.trigger(`${NAMESPACE}::${eventType}`, data);

    // Callback name - allow only words, capitalize event type string and prepend the 'on' string,
    const onFunction = `on${eventType.replace(/\b\w+\b/g, word => word.substring(0, 1).toUpperCase() + word.substring(1))}`;

    if (typeof this.options[onFunction] === 'function') {
      this.options[onFunction].apply(this, params);
    }
  }

  destroy() {
    this.$element.data(NAMESPACE, null);
    this.trigger('destroy');
  }

  static setDefaults(options) {
    $.extend(true, DEFAULTS, $.isPlainObject(options) && options);
  }
}

export default CFDynamicMenu;



// WEBPACK FOOTER //
// ./assets/js/cf-dynamic-menu/CFDynamicMenu.js